import { useState, useEffect } from 'react'
import { NavLink } from 'react-router'
import { useTheme } from '../hooks/useTheme'

function useClock() {
  const [time, setTime] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  return time
}

const NAV_LINKS = [
  { to: '/',          label: 'Kaufplan',  end: true  },
  { to: '/einstiege', label: 'Einstiege', end: false },
  { to: '/makro',     label: 'Makro',     end: false },
  { to: '/archiv',    label: 'Archiv',    end: false },
]

function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M3.4 12.6l1.1-1.1M11.5 4.5l1.1-1.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M13.5 9A6 6 0 0 1 7 2.5a5.5 5.5 0 1 0 6.5 6.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

export default function Nav() {
  const [open, setOpen] = useState(false)
  const { theme, toggle } = useTheme()
  const time = useClock()
  const timeStr = time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })

  return (
    <nav
      className="sticky top-0 z-50"
      style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="max-w-[1200px] mx-auto px-5 flex items-center h-[52px] gap-6">

        {/* Logo */}
        <NavLink
          to="/"
          className="shrink-0 font-mono text-[13px] font-bold tracking-tight"
          onClick={() => setOpen(false)}
        >
          <span style={{ color: 'var(--accent)' }}>Market</span>
          <span style={{ color: 'var(--text-2)' }}>Intel</span>
        </NavLink>

        {/* Divider */}
        <div className="hidden md:block w-px h-5 shrink-0" style={{ background: 'var(--border-hi)' }} aria-hidden="true" />

        {/* Desktop links */}
        <ul className="hidden md:flex items-center gap-0.5 flex-1 list-none m-0 p-0">
          {NAV_LINKS.map(({ to, label, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  `font-mono text-[11.5px] px-2.5 py-1.5 rounded-md transition-colors duration-150 ${
                    isActive
                      ? 'font-semibold'
                      : 'hover:text-t2'
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive ? 'var(--text-1)' : 'var(--text-3)',
                  background: isActive ? 'var(--surface-2)' : undefined,
                })}
              >
                {label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Right: time + theme toggle */}
        <div className="flex items-center gap-3 ml-auto">
          <span
            className="hidden lg:block font-mono text-[11px] tabular-nums"
            style={{ color: 'var(--text-3)' }}
          >
            {timeStr}
          </span>

          <button
            onClick={toggle}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
            style={{
              color: 'var(--text-3)',
              background: 'transparent',
              border: '1px solid var(--border)',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-hi)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-1)' }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-3)' }}
            aria-label={theme === 'dark' ? 'Zu Light Mode wechseln' : 'Zu Dark Mode wechseln'}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* Hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
            style={{
              color: 'var(--text-3)',
              border: '1px solid var(--border)',
            }}
            onClick={() => setOpen(o => !o)}
            aria-label={open ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              {open
                ? <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                : <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              }
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          id="mobile-nav"
          className="md:hidden px-4 pb-4 pt-2 flex flex-col gap-1 animate-fade-in-up"
          style={{ borderTop: '1px solid var(--border)', background: 'var(--surface)' }}
        >
          <div className="flex items-center justify-between px-2.5 py-2 mb-1">
            <span className="font-mono text-[11px] tabular-nums" style={{ color: 'var(--text-3)' }}>{timeStr}</span>
            <button
              onClick={toggle}
              className="font-mono text-[10px] px-2 py-1.5 min-h-[24px] rounded"
              style={{ color: 'var(--accent)', background: 'var(--accent-bg)', border: '1px solid rgba(101,116,248,0.2)' }}
            >
              {theme === 'dark' ? '☀ Light' : '☾ Dark'}
            </button>
          </div>
          {NAV_LINKS.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `font-mono text-[12px] px-3 py-3 rounded-lg min-h-[44px] flex items-center transition-colors ${
                  isActive ? 'font-semibold' : ''
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? 'var(--text-1)' : 'var(--text-3)',
                background: isActive ? 'var(--surface-2)' : 'transparent',
              })}
            >
              {label}
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  )
}
